import mapboxgl from 'mapbox-gl';
import _ from 'lodash';

import {
  RouteItemStateType,
  RouteStateTypes,
  SourceImportType,
} from '~globals/types/enums';
import { ApiTransactionRouteItem } from '~services/apiTransaction/types';
import { AuthExtendedUser } from '~services/auth/types';
import { Nullable } from '~globals/types';

import { isAdminUser } from './user';

export const routeStopOrderPendingList = [
  RouteItemStateType.New,
  RouteItemStateType.OnAgenda,
  RouteItemStateType.InProgress,
];

export const routeStopOrderCompleteList = [
  RouteItemStateType.FinalizedSuccess,
  RouteItemStateType.FinalizedError,
  RouteItemStateType.WithoutVisiting,
];

export const routeStopOrderFinalizedList = [
  RouteItemStateType.FinalizedSuccess,
  RouteItemStateType.FinalizedError,
];

export const assignedDriverEnabledList = [
  RouteStateTypes.New,
  RouteStateTypes.Rejected,
];

export const deleteRouteEnabledListForAdmin = [
  RouteStateTypes.InProgress,
  RouteStateTypes.Finalized,
];

export const exportTicketsEnabledList = [
  RouteStateTypes.New,
  RouteStateTypes.Assigned,
  RouteStateTypes.Approve,
  RouteStateTypes.Rejected,
  RouteStateTypes.InProgress,
];

export const updatedRouteEnabledList = [
  RouteStateTypes.New,
  RouteStateTypes.Assigned,
  RouteStateTypes.Approve,
  RouteStateTypes.Rejected,
  RouteStateTypes.InProgress,
];

export const setFeedbackEnabledList = [
  RouteStateTypes.InProgress,
  RouteStateTypes.Finalized,
  RouteStateTypes.Expired,
];

export const validateRouteStateTypeId = (
  state: RouteStateTypes,
  validStates: RouteStateTypes[],
): boolean => validStates.includes(state);

export const showFeedbackEnabledList = [RouteStateTypes.Finalized];

export const startRouteEnabledList = [
  RouteStateTypes.Assigned,
  RouteStateTypes.Approve,
];

export const isRouteStopOrderPending = (state: RouteItemStateType): boolean =>
  routeStopOrderPendingList.includes(state);

export const isRouteStopOrderComplete = (state: RouteItemStateType): boolean =>
  routeStopOrderCompleteList.includes(state);

export const isRouteStopOrderFinalized = (state: RouteItemStateType): boolean =>
  routeStopOrderFinalizedList.includes(state);

export interface StopRevisitInfo {
  isRevisit: boolean;
  visitNumber: number;
}

/**
 * Derives whether a route stop is a revisit and which visit number it is,
 * from the stop state and the order's `visitCount`.
 *
 * `visitCount` is incremented when a stop is closed and counted (COMPLETED /
 * REJECTED). So for already-counted stops the current visit is `visitCount`
 * and it is a revisit when `visitCount > 1`; for open/without-visit stops the
 * current visit is `visitCount + 1` and it is a revisit when `visitCount > 0`.
 * A `null` `visitCount` is treated as `0`.
 */
export const getStopRevisitInfo = (
  routeItemStateTypeId: RouteItemStateType,
  visitCount: Nullable<number>,
): StopRevisitInfo => {
  const currentVisitCount = visitCount ?? 0;

  if (isRouteStopOrderFinalized(routeItemStateTypeId)) {
    return {
      isRevisit: currentVisitCount > 1,
      visitNumber: currentVisitCount,
    };
  }

  return {
    isRevisit: currentVisitCount > 0,
    visitNumber: currentVisitCount + 1,
  };
};

export const isEnabledToAssginedDriver = (state: RouteStateTypes): boolean =>
  assignedDriverEnabledList.includes(state);

export const isEnabledShowFeedback = (state: RouteStateTypes): boolean =>
  showFeedbackEnabledList.includes(state);

export const getRouteItemCoords = (
  routeItem: ApiTransactionRouteItem,
): mapboxgl.LngLat => {
  const longitude = (routeItem.warehouse?.longitude ??
    routeItem.item?.longitude) as number;
  const latitude = (routeItem.warehouse?.latitude ??
    routeItem.item?.latitude) as number;

  return new mapboxgl.LngLat(longitude, latitude);
};

export const validateRouteItemDistanceFromAddress = (
  comparator: 'gt' | 'gte' | 'lt' | 'lte',
  distance: number,
  maxDistance: number,
): boolean => {
  const comparatorFunc = _[comparator];

  return comparatorFunc(distance, maxDistance);
};

export const isRouteEnabledToDelete = (
  user: Nullable<AuthExtendedUser>,
  routeState: RouteStateTypes,
): boolean => {
  if (!isAdminUser(user)) {
    return !deleteRouteEnabledListForAdmin.includes(routeState);
  }

  return true;
};

export const isRouteEnabledToExportTickets = (
  state: RouteStateTypes,
): boolean => exportTicketsEnabledList.includes(state);

export const isRouteEnabldeToUpdate = (state: RouteStateTypes): boolean =>
  updatedRouteEnabledList.includes(state);

export const isRouteToEnabledSetFeedback = (state: RouteStateTypes): boolean =>
  setFeedbackEnabledList.includes(state);

export const isRouteToEnabledStart = (state: RouteStateTypes): boolean =>
  startRouteEnabledList.includes(state);

export const isRouteInProgress = (state: RouteStateTypes): boolean =>
  state === RouteStateTypes.InProgress;

export const isRouteFinalized = (state: RouteStateTypes): boolean =>
  state === RouteStateTypes.Finalized;

export const getRoutesStopsInfoByApiTransaction = (
  routeItems: ApiTransactionRouteItem[],
): {
  total: number;
  pendings: number;
  completedSuccess: number;
  completedFails: number;
  withoutVisiting: number;
  flex: number;
  finalized: number;
} =>
  routeItems.reduce(
    (prev, current) => {
      if (
        current.routeItemStateTypeId === RouteItemStateType.FinalizedSuccess
      ) {
        prev.completedSuccess += 1;
        prev.finalized += 1;
      }

      if (current.routeItemStateTypeId === RouteItemStateType.FinalizedError) {
        prev.completedFails += 1;
        prev.finalized += 1;
      }

      if (current.routeItemStateTypeId === RouteItemStateType.WithoutVisiting) {
        prev.withoutVisiting += 1;
        prev.finalized += 1;
      }

      if (
        current.item &&
        current.item?.sourceImportTypeId === SourceImportType.MFlex
      ) {
        prev.flex += 1;
      }

      if (isRouteStopOrderPending(current.routeItemStateTypeId)) {
        prev.pendings += 1;
      }

      prev.total += 1;

      return prev;
    },
    {
      total: 0,
      pendings: 0,
      completedSuccess: 0,
      completedFails: 0,
      withoutVisiting: 0,
      flex: 0,
      finalized: 0,
    },
  );
